import type { FC } from 'react';
import React from 'react';
import type { TConfirmationState } from '@alfa-bank/site-confirmation/src/models';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import {
    setStep,
    changeState,
    smsConfirmRequest,
    smsResend,
    selectAppData,
    selectDraftData,
    selectFirstStepData,
    selectSmsData,
} from '@alfa-bank/site-dc-form-api/store';
import type { TDispatch } from '@alfa-bank/site-dc-form-api/src/store/features/models';

import { formatPhoneNumber } from './utils/helpers';
import { useAnalytics } from './utils/use-analytics';
import { FATAL_ERROR_DESCRIPTION, LINK_TO_HINT } from './constants';
import type { TSmsVerification } from './models';

export const SmsConfirmation: FC<TSmsVerification> = ({
    Button,
    Confirmation,
    Text,
    countdownDuration,
    isMobile,
    styler,
    styles,
}) => {
    const { mobilePhone } = useSelector(selectFirstStepData);
    const { changePhoneButtonVisible } = useSelector(selectAppData);
    const { shortMobilePhone } = useSelector(selectDraftData);
    const { screen, state } = useSelector(selectSmsData);
    const dispatch = useDispatch<TDispatch>();
    const { analytics, sendEventOnFinished, sendEventOnRepeat } = useAnalytics(countdownDuration);

    const handleOnRepeat = () => {
        dispatch(smsResend());

        sendEventOnRepeat();
    };

    const handleOnFinished = async (code: string) => {
        const resStatus = await dispatch(smsConfirmRequest(code)).unwrap();

        sendEventOnFinished(resStatus);
    };

    const phone = mobilePhone
        ? formatPhoneNumber(mobilePhone?.toString(), true)
        : formatPhoneNumber(shortMobilePhone?.toString());

    return (
        <Confirmation
            alignContent="center"
            className={cn(styles.smsConfirmation, styles.external)}
            countdownDuration={countdownDuration}
            dataTestId="dc-form-sms-confirmation"
            initialScreenHintSlot={
                changePhoneButtonVisible ? (
                    <Button
                        dataTestId="change-phone"
                        onClick={() => {
                            dispatch(setStep('changePhoneNumber'));

                            sendEvent(analytics.changePhoneButton.click);
                        }}
                        size="xxs"
                        styler={{ marginTop: isMobile ? 12 : undefined }}
                        view="tertiary"
                    >
                        <Text styler={{ margin: 0, padding: 0 }} view="primary-small">
                            Изменить номер телефона
                        </Text>
                    </Button>
                ) : (
                    <></>
                )
            }
            mobile={isMobile}
            onChangeState={confirmationState => {
                dispatch(changeState(confirmationState as TConfirmationState));
            }}
            onFatalErrorOkButtonClick={handleOnRepeat}
            onInputFinished={handleOnFinished}
            onSmsRetryClick={handleOnRepeat}
            phone={phone}
            requiredCharAmount={5}
            screen={screen}
            state={state}
            styler={styler}
            texts={{
                title: 'Введите код из смс',
                linkToHint: LINK_TO_HINT,
                fatalErrorDescription: FATAL_ERROR_DESCRIPTION,
            }}
        />
    );
};
